//@author: sara
import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { S25Durationfilter } from "../modules/s25-editable/s25-editable-duration/s25.durationfilter.service";
import { S25Datefilter } from "../modules/s25-dateformat/s25.datefilter.service";
import { DurationI } from "../pojo/DurationI";
import { Proto } from "../pojo/Proto";
import { S25Util } from "../util/s25-util";

@Pipe({
    name: "safeLink",
    standalone: false,
})
export class SafeLink implements PipeTransform {
    constructor(private sanitizer: DomSanitizer) {}
    transform(url: string): any {
        return this.sanitizer.bypassSecurityTrustUrl(url);
    }
}

//we should be able to add more @pipe classes here
@Pipe({
    name: "durationPipe",
    standalone: false,
})
export class DurationPipe implements PipeTransform {
    transform(duration: DurationI | Proto.ISODurationString): string {
        return S25Durationfilter.duration(duration);
    }
}

@Pipe({
    name: "SortBy",
    standalone: false,
})
export class SortByPipe implements PipeTransform {
    transform(values: Array<any>, args?: string) {
        return args === "ASC" ? values && values.sort() : values && values.sort().reverse();
    }
}

@Pipe({
    name: "dateFormat",
    standalone: false,
})
export class DateFormatPipe implements PipeTransform {
    transform(date: any, format?: string) {
        return S25Datefilter.transform(date, format);
    }
}

@Pipe({
    name: "timeFormat",
    standalone: false,
})
export class TimeFormatPipe implements PipeTransform {
    transform(date: any, format?: string) {
        return S25Datefilter.transform(date, format);
    }
}

// keeps HTML sanitizer from removing CSS from rich text editor contents
@Pipe({
    name: "safeHTML",
    pure: false,
    standalone: false,
})
export class HTMLPipe implements PipeTransform {
    // avoid updating templates if html has already been sanitized to avoid unnecessary rerenders, disrupting text highlighting for example
    private cache = new Map<string, SafeHtml>();

    constructor(protected _sanitizer: DomSanitizer) {}

    transform(value: string): SafeHtml | undefined {
        if (!value) return;

        if (this.cache.has(value)) {
            return this.cache.get(value);
        }

        const sanitized = this._sanitizer.bypassSecurityTrustHtml(value);
        this.cache.set(value, sanitized);

        // Limit cache size to prevent memory leaks
        if (this.cache.size > 100) {
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
        }

        return sanitized;
    }
}

@Pipe({
    name: "currency",
    standalone: false,
})
export class CurrencyFormatPipe implements PipeTransform {
    transform(number: number) {
        const formatter = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        });

        return formatter.format(number);
    }
}

@Pipe({
    name: "capitalizeBoundaries",
    standalone: true,
})
//ANG-7689 capitalize after white space or period
export class CapitalizeBoundariesPipe implements PipeTransform {
    transform(value: string | null | undefined): string {
        if (!value) return "";
        return value.replace(/(^|[\s.])([a-z])/g, (_, prefix, letter) => prefix + letter.toUpperCase());
    }
}

@Pipe({ name: "pluralS", standalone: true })
export class PluralSPipe implements PipeTransform {
    transform(message: string, count: number) {
        return count === 1 ? message : message + "s";
    }
}

@Pipe({ name: "ordinalSuffix", standalone: true })
export class OrdinalSuffixPipe implements PipeTransform {
    transform(num: number): string {
        return `${num}${S25Util.getOrdinalSuffix(num)}`;
    }
}

@Pipe({ name: "markdown", standalone: true })
export class SimpleMarkdownPipe implements PipeTransform {
    transform(value: string): string {
        if (!value) return "";
        let html = value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
            .replace(/\*(.+?)\*/g, "<em>$1</em>")
            .replace(/\n/g, "<br>");
        html = html.replace(/((?:^|<br>)\s*- .+(?:<br>\s*- .+)*)/g, (match) => {
            const items = match
                .split(/<br>/)
                .map((line) => line.replace(/^\s*- /, "").trim())
                .filter((line) => line)
                .map((line) => `<li>${line}</li>`)
                .join("");
            return `<ul>${items}</ul>`;
        });
        return html;
    }
}
